/**
 * Phase 28 — the verdict's story, told once per 14 s cycle with the draft's
 * own numbers (user, 12/10/2026: "make animation to describe in the most
 * powerful way"): the drops send → the stats settle ({delay} min) → the
 * judge reads ≥ {sample} SMS → PASS keeps the list, FAIL burns it (21/10/2026:
 * no day counter any more) → with Stop list the rest of today stops → the
 * burned list leaves the order → tomorrow the next one; with scaling on, a performing day grows
 * the next drops, a weak one shrinks them (Phase 31); with rest days set,
 * the burned list comes back after them with its test amount (Phase 31).
 * Each node appears at its turn and stays; reduced motion sees it all at once.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { ArrowRight, Ban, CheckCircle2, Flame, ListOrdered, RotateCcw, Scale, Timer, TrendingDown, TrendingUp, XCircle } from 'lucide-react';

import type { DropGenTextMix } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { ProvenTag } from '../../../shared/texts/ProvenTag';

const NODE = 'inline-flex items-center gap-1.5 rounded-md border bg-background px-2 py-1.5 text-xs animate-dg-story-in motion-reduce:animate-none motion-reduce:opacity-100';
const ARROW = 'h-3.5 w-3.5 shrink-0 text-muted-foreground/60';

const Node: FC<{ at: number; className?: string; children: ReactNode }> = ({ at, className, children }) => (
  <span className={cn(NODE, className ?? 'border-border')} style={{ animationDelay: `${at}s` }}>
    {children}
  </span>
);

interface Props {
  delayMin: number;
  minSample: number;
  failMode: string;
  /** 20/10/2026 — FAIL only when this many drops each met the rule on their own numbers. */
  timesN?: number;
  /** 20/10/2026 — those drops in total, or in a row. */
  timesMode?: 'TOTAL' | 'ROW';
  scaling?: { stepPct: number; maxAmount: number | null } | null;
  /** Phase 31 — a weak rule is set: the next drops shrink. */
  shrink?: { shrinkPct: number } | null;
  /** Phase 31 — rest days are set: the burned list comes back (Phase 37: proven = the text policy's share on its test day). */
  rest?: { days: number; testAmount: number | null; proven?: DropGenTextMix } | null;
}

export const VerdictStory: FC<Props> = ({ delayMin, minSample, failMode, timesN = 1, timesMode = 'TOTAL', scaling, shrink, rest }) => {
  const stops = failMode === 'STOP_FOR_DAY';
  return (
    <div className="rounded-lg border border-border/60 bg-muted/20 p-3" data-testid="verdict-story" style={{ '--dg-story': '14s' } as React.CSSProperties}>
      <div className="flex flex-wrap items-center gap-2">
        <Node at={0}>
          <span className="flex items-center gap-0.5" aria-hidden>
            {[0, 0.4, 0.8, 1.2, 1.6].map((at) => (
              <span key={at} className="h-1.5 w-1.5 rounded-full bg-primary animate-dg-story-in motion-reduce:animate-none motion-reduce:opacity-100" style={{ animationDelay: `${at}s` }} />
            ))}
          </span>
          the group&apos;s drops send
        </Node>
        <ArrowRight className={ARROW} />
        <Node at={2}>
          <Timer className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <span className="flex flex-col gap-0.5">
            <span>{delayMin > 0 ? `stats settle · ${delayMin} min after each send` : 'stats come in after each send'}</span>
            <span className="h-1 w-24 overflow-hidden rounded bg-muted" aria-hidden>
              <span className="block h-full bg-primary/60 animate-dg-story-fill motion-reduce:animate-none motion-reduce:w-full" style={{ animationDelay: '2.2s' }} />
            </span>
          </span>
        </Node>
        <ArrowRight className={ARROW} />
        <Node at={3.2}>
          <Scale className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          the rule runs{minSample > 0 ? ` · needs ≥ ${minSample.toLocaleString('en-US')} settled SMS` : ''}
          {timesN > 1 ? ` · FAIL once ${timesN} drops${timesMode === 'ROW' ? ' in a row' : ''} each met it` : ''}
        </Node>
        <ArrowRight className={ARROW} />
        <span className="flex flex-col gap-1">
          <Node at={4.2} className="border-success/40">
            <CheckCircle2 className="h-3.5 w-3.5 text-success" aria-hidden />
            <b>PASS</b>
            <span className="text-muted-foreground">— keeps its list{scaling ? ', may grow' : ''}{shrink ? ', may shrink' : ''}</span>
          </Node>
          <Node at={5.4} className="border-destructive/40">
            <XCircle className="h-3.5 w-3.5 text-destructive" aria-hidden />
            <b>FAIL</b>
            <span className="text-muted-foreground">— burns the list{stops ? ' · the rest of today stops' : ' · today finishes'}</span>
          </Node>
        </span>
        <ArrowRight className={ARROW} />
        <Node at={6.8} className="border-destructive/40">
          <Flame className="h-3.5 w-3.5 text-destructive" aria-hidden />
          burned — leaves this container&apos;s order
        </Node>
        <ArrowRight className={ARROW} />
        <Node at={8}>
          <ListOrdered className="h-3.5 w-3.5 text-primary" aria-hidden />
          tomorrow: the group&apos;s next list
        </Node>
        {stops ? (
          <Node at={5.9} className="border-warning/40">
            <Ban className="h-3.5 w-3.5 text-warning" aria-hidden />
            Stop list — the group&apos;s remaining drafts stop
          </Node>
        ) : null}
        {rest ? (
          <Node at={9.2} className="border-warning/40">
            <RotateCcw className="h-3.5 w-3.5 text-warning" aria-hidden />
            back after {rest.days} day{rest.days === 1 ? '' : 's'} of rest{rest.testAmount !== null ? ` · ${rest.testAmount.toLocaleString('en-US')} SMS to try it` : ''} — a FAIL burns it again
            <ProvenTag mix={rest.proven} what="The returning list's test-day drops" />
          </Node>
        ) : null}
        {scaling ? (
          <Node at={rest ? 10.4 : 9.2} className="border-success/40">
            <TrendingUp className="h-3.5 w-3.5 text-success" aria-hidden />
            performing: the next drops grow +{scaling.stepPct} % per check{scaling.maxAmount !== null ? ` · up to ${scaling.maxAmount.toLocaleString('en-US')} SMS` : ''}
          </Node>
        ) : null}
        {shrink ? (
          <Node at={rest ? 11.4 : 10.2} className="border-warning/40">
            <TrendingDown className="h-3.5 w-3.5 text-warning" aria-hidden />
            weak: the next drops shrink −{shrink.shrinkPct} % per check
          </Node>
        ) : null}
      </div>
    </div>
  );
};
